/**
 * Editor View-Model — the PURE layer between `seoScore.ts` and the live
 * editor UI (ContentEngineView → LiveSeoEditorPanel).
 *
 * Everything the panel renders (dimension rows, keyword chips, fix cards,
 * SERP snippet, AEO/JSON-LD preview, word counts) and every state
 * transition a button dispatches (`applyFix`, `diffScore`) lives here so
 * the whole Surfer/Clearscope-style UX is headlessly testable. The React
 * layer stays thin: render this model, dispatch these functions.
 *
 * PURE by contract (same as seoScore): no store, no firebase, no network,
 * no clock, no DOM, no randomness. `applyFix` NEVER mutates its input —
 * it returns a brand-new EditorState so before/after diffing (and undo
 * history, mirroring the timeMachine restore pattern) is trivial.
 */

import {
  scoreContent,
  detectAEO,
  pixelWidth,
  buildFaqJsonLd,
  buildArticleJsonLd,
  TITLE_CHAR_MAX,
  TITLE_PIXEL_LIMIT,
  META_CHAR_MAX,
  META_PIXEL_LIMIT,
  AEO_ANSWER_WORD_LIMIT,
  type KeywordTarget,
  type ScoreInput,
  type Finding,
  type SeoScoreResult,
} from './seoScore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Everything the live editor holds. A plain serializable value object. */
export interface EditorState {
  title: string;
  meta: string;
  markdown: string;
  targets: KeywordTarget[];
  corpus?: { slug: string; title: string; terms: string[] }[];
  brandFacts?: ScoreInput['brandFacts'];
  typicalScore?: number;
}

/** Render-ready model for the right-hand live panel. */
export interface PanelViewModel {
  grade: string;
  total: number;
  dimensions: {
    key: string;
    label: string;
    score: number;
    max: number;
    state: 'red' | 'yellow' | 'green';
    findings: Finding[];
  }[];
  termChips: {
    term: string;
    count: number;
    target: string;
    state: 'red' | 'yellow' | 'green';
  }[];
  fixCards: {
    id: string;
    label: string;
    expectedGain: number;
    kind: string;
    enabled: boolean;
  }[];
  wordCount: number;
  charCount: number;
}

/** SERP snippet preview — truncation computed with the same pixel table
 *  the scorer lints with, so preview and score never disagree. */
export interface SerpPreview {
  titleDisplay: string;
  titleTruncated: boolean;
  metaDisplay: string;
  metaTruncated: boolean;
  urlDisplay: string;
}

/** Answer-engine preview: the extractable answer block, Q&A pairs, and
 *  the JSON-LD artifacts that make AEO a visible deliverable. */
export interface AeoPreview {
  answerBlock: string | null;
  faqPairs: { q: string; a: string }[];
  faqJsonLd: string;
  articleJsonLd: string;
}

// ---------------------------------------------------------------------------
// Internal helpers (deterministic, no DOM)
// ---------------------------------------------------------------------------

const DIMENSION_LABELS: Record<string, string> = {
  title: 'Title tag',
  meta: 'Meta description',
  headings: 'Heading structure',
  keywords: 'Keyword coverage',
  aeo: 'Answer engine (AEO)',
  readability: 'Readability',
  internalLinks: 'Internal links',
  brandFidelity: 'Brand fidelity',
};

/** Fix kinds `applyFix` knows how to execute — all of FixSuggestion.kind. */
const SUPPORTED_FIX_KINDS = new Set([
  'add-term',
  'shorten-title',
  'add-answer',
  'split-paragraph',
  'add-heading',
  'add-faq-schema',
  'add-internal-link',
]);

function stateForScore(score: number): 'red' | 'yellow' | 'green' {
  if (score >= 80) return 'green';
  if (score >= 50) return 'yellow';
  return 'red';
}

/** Markdown → plain prose, mirroring the scorer's strip (kept local so this
 *  module stays decoupled from seoScore internals). */
function stripMd(md: string): string {
  return (md ?? '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\|/g, ' ')
    .replace(/[*_>]/g, '');
}

function splitWords(text: string): string[] {
  return text.split(/\s+/).filter((w) => w.length > 0);
}

interface HeadingLine {
  level: number;
  text: string;
  line: number;
}

/** Parse ATX headings with their source line numbers. Exported so the view
 *  can render the outline sidebar from the exact same parse. */
export function parseOutline(markdown: string): HeadingLine[] {
  const out: HeadingLine[] = [];
  const lines = (markdown ?? '').split('\n');
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^(#{1,6})\s+(.*\S)\s*$/);
    if (m) out.push({ level: m[1].length, text: m[2].trim(), line: i });
  }
  return out;
}

/** First prose paragraph after a heading line (stops at the next heading). */
function firstParagraphAfter(lines: string[], headingLine: number): string {
  const para: string[] = [];
  for (let i = headingLine + 1; i < lines.length; i++) {
    const line = lines[i];
    if (/^#{1,6}\s/.test(line)) break;
    if (line.trim().length === 0) {
      if (para.length > 0) break;
      continue;
    }
    para.push(line.trim());
  }
  return para.join(' ');
}

function toScoreInput(state: EditorState): ScoreInput {
  return {
    title: state.title,
    meta: state.meta,
    markdown: state.markdown,
    targets: state.targets,
    internalLinkCorpus: state.corpus,
    brandFacts: state.brandFacts,
    typicalScore: state.typicalScore,
  };
}

function scoreState(state: EditorState): SeoScoreResult {
  return scoreContent(toScoreInput(state));
}

/** Trim text to fit a char + pixel bound at a word boundary, appending an
 *  ellipsis the way the SERP does. Returns the text unchanged if it fits. */
function truncateForSerp(
  text: string,
  kind: 'title' | 'meta',
  charMax: number,
  pxLimit: number,
): { display: string; truncated: boolean } {
  const t = (text ?? '').trim();
  if (t.length <= charMax && pixelWidth(t, kind) <= pxLimit) {
    return { display: t, truncated: false };
  }
  let cut = t.slice(0, charMax);
  while (cut.length > 0 && pixelWidth(`${cut}…`, kind) > pxLimit) {
    cut = cut.slice(0, -1);
  }
  // Prefer breaking at the last word boundary so the snippet reads clean.
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > charMax * 0.5) cut = cut.slice(0, lastSpace);
  return { display: `${cut.trimEnd()}…`, truncated: true };
}

/** Deterministic slug for the SERP url line. */
function slugify(text: string): string {
  const slug = (text ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 48)
    .replace(/-$/, '');
  return slug.length > 0 ? slug : 'untitled';
}

/** Extractable Q&A pairs, gated by detectAEO's own ok findings so preview
 *  and score can never disagree about what an answer engine would lift. */
function extractQaPairs(markdown: string): { q: string; a: string }[] {
  const sub = detectAEO(markdown ?? '');
  const okLines = new Set(
    sub.findings
      .filter((f) => f.severity === 'ok' && f.id.startsWith('aeo-answer-first-'))
      .map((f) => Number(f.id.slice('aeo-answer-first-'.length))),
  );
  if (okLines.size === 0) return [];
  const lines = (markdown ?? '').split('\n');
  return parseOutline(markdown)
    .filter((h) => h.level === 2 && okLines.has(h.line))
    .map((h) => ({ q: h.text, a: stripMd(firstParagraphAfter(lines, h.line)).trim() }));
}

// ---------------------------------------------------------------------------
// Public pure functions
// ---------------------------------------------------------------------------

/** Prose word count (markdown syntax stripped) — words, not characters. */
export function wordCount(markdown: string): number {
  return splitWords(stripMd(markdown ?? '')).length;
}

/** Score the state and map it into everything the live panel renders. */
export function buildPanelViewModel(state: EditorState): PanelViewModel {
  const result = scoreState(state);
  return {
    grade: result.grade,
    total: result.total,
    dimensions: result.subScores.map((s) => ({
      key: s.dimension,
      label: DIMENSION_LABELS[s.dimension] ?? s.dimension,
      score: s.score,
      max: s.max,
      state: stateForScore(s.score),
      findings: s.findings,
    })),
    termChips: result.terms.map((t) => ({
      term: t.term,
      count: t.count,
      target: `${t.min}–${t.max}×`,
      state: t.state,
    })),
    fixCards: result.prioritizedFixes.map((f) => ({
      id: f.id,
      label: f.label,
      expectedGain: f.expectedGain,
      kind: f.kind,
      enabled: SUPPORTED_FIX_KINDS.has(f.kind),
    })),
    wordCount: wordCount(state.markdown),
    charCount: (state.markdown ?? '').length,
  };
}

/** SERP snippet preview using the scorer's own pixel table for truncation. */
export function buildSerpPreview(state: EditorState): SerpPreview {
  const title = truncateForSerp(state.title, 'title', TITLE_CHAR_MAX, TITLE_PIXEL_LIMIT);
  const meta = truncateForSerp(state.meta, 'meta', META_CHAR_MAX, META_PIXEL_LIMIT);
  return {
    titleDisplay: title.display,
    titleTruncated: title.truncated,
    metaDisplay: meta.display,
    metaTruncated: meta.truncated,
    urlDisplay: `example.com › blog › ${slugify(state.title)}`,
  };
}

/** Answer-engine preview: extractable answer + FAQ/Article JSON-LD. */
export function buildAeoPreview(state: EditorState): AeoPreview {
  const pairs = extractQaPairs(state.markdown);
  return {
    answerBlock: pairs.length > 0 ? pairs[0].a : null,
    faqPairs: pairs,
    faqJsonLd: buildFaqJsonLd(pairs),
    articleJsonLd: buildArticleJsonLd({ title: state.title, meta: state.meta }),
  };
}

/** Before/after score diff — powers the visible delta toast per fix and
 *  section-rewrite comparisons. delta === to - from, always. */
export function diffScore(
  before: EditorState,
  after: EditorState,
): { delta: number; from: number; to: number } {
  const from = scoreState(before).total;
  const to = scoreState(after).total;
  return { delta: to - from, from, to };
}

// ---------------------------------------------------------------------------
// applyFix — one deterministic in-editor transform per FixSuggestion.kind
// ---------------------------------------------------------------------------

/** Trim to a char bound at a word boundary (never mid-word when avoidable). */
function trimToChars(text: string, charMax: number): string {
  const t = text.trim();
  if (t.length <= charMax) return t;
  let cut = t.slice(0, charMax);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > charMax * 0.5) cut = cut.slice(0, lastSpace);
  return cut.replace(/[\s:,;—–-]+$/, '');
}

function fixAddTerm(state: EditorState, payload: Record<string, unknown>): EditorState {
  const term = String(payload.term ?? '').trim();
  if (term.length === 0) return { ...state };
  const where = payload.where;
  if (where === 'title') {
    const title = state.title.trim();
    return { ...state, title: title.length === 0 ? term : `${term}: ${title}` };
  }
  if (where === 'h1') {
    const lines = state.markdown.split('\n');
    const idx = lines.findIndex((l) => /^#\s+\S/.test(l));
    if (idx === -1) return { ...state, markdown: `# ${term}\n\n${state.markdown}` };
    lines[idx] = `${lines[idx]} — ${term}`;
    return { ...state, markdown: lines.join('\n') };
  }
  // Body insertion: a bracketed placeholder carrying the term the exact
  // number of times coverage still needs, so the chip goes green while the
  // writer can see precisely what to naturalize.
  const add = Math.max(1, Number(payload.add ?? 1) || 1);
  const mentions = new Array(add).fill(`work "${term}" into this section`).join('; ');
  return { ...state, markdown: `${state.markdown}\n\n[Draft note: ${mentions}]` };
}

function fixShorten(state: EditorState, payload: Record<string, unknown>): EditorState {
  if (payload.field === 'meta') {
    let meta = trimToChars(state.meta, META_CHAR_MAX);
    while (meta.length > 0 && pixelWidth(meta, 'meta') > META_PIXEL_LIMIT) {
      meta = trimToChars(meta, meta.length - 1);
    }
    return { ...state, meta };
  }
  let title = trimToChars(state.title, TITLE_CHAR_MAX);
  while (title.length > 0 && pixelWidth(title, 'title') > TITLE_PIXEL_LIMIT) {
    title = trimToChars(title, title.length - 1);
  }
  return { ...state, title };
}

function fixAddAnswer(state: EditorState, payload: Record<string, unknown>): EditorState {
  const heading = String(payload.heading ?? '');
  const lines = state.markdown.split('\n');
  const h = parseOutline(state.markdown).find((x) => x.text === heading);
  if (!h) return { ...state };
  const answer = `[Direct answer: answer "${heading}" in one or two plain sentences, under ${AEO_ANSWER_WORD_LIMIT} words.]`;
  lines.splice(h.line + 1, 0, '', answer);
  return { ...state, markdown: lines.join('\n') };
}

function fixSplitParagraph(state: EditorState): EditorState {
  const blocks = state.markdown.split(/\n\s*\n/);
  let bestIdx = -1;
  let bestWords = 0;
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (/^\s*#{1,6}\s/.test(b) || /^\s*(?:```|[-*+]\s|\d+\.\s|\|)/.test(b)) continue;
    const n = splitWords(stripMd(b)).length;
    if (n > bestWords) {
      bestWords = n;
      bestIdx = i;
    }
  }
  if (bestIdx === -1) return { ...state };
  const block = blocks[bestIdx];
  // Split at the sentence boundary nearest the midpoint.
  const boundaries: number[] = [];
  const re = /[.!?]\s+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(block)) !== null) boundaries.push(m.index + 1);
  if (boundaries.length === 0) return { ...state };
  const mid = block.length / 2;
  const cut = boundaries.reduce((a, b) => (Math.abs(b - mid) < Math.abs(a - mid) ? b : a));
  blocks[bestIdx] = `${block.slice(0, cut).trim()}\n\n${block.slice(cut).trim()}`;
  return { ...state, markdown: blocks.join('\n\n') };
}

function fixAddHeading(state: EditorState, payload: Record<string, unknown>): EditorState {
  const md = state.markdown;
  // Missing H1 → lead the doc with one built from the working title.
  if (payload.level === 1 && !payload.shape && !payload.after) {
    const h1 = state.title.trim().length > 0 ? state.title.trim() : '[Page title]';
    return { ...state, markdown: `# ${h1}\n\n${md}` };
  }
  // Question-shaped H2 with an answer-first placeholder so it is
  // immediately extractable (not just a bare heading).
  if (payload.shape === 'question') {
    const scaffold = `\n\n## How does it work?\n\n[Answer this in one short paragraph — under ${AEO_ANSWER_WORD_LIMIT} words — leading with the direct answer.]`;
    return { ...state, markdown: `${md}${scaffold}` };
  }
  // Skipped level → insert the bridging heading before the offender.
  if (typeof payload.after === 'string') {
    const level = Math.min(6, Math.max(2, Number(payload.level) || 2));
    const outline = parseOutline(md);
    const anchor = outline.find((h) => h.text === payload.after);
    if (anchor) {
      const next = outline.find((h) => h.line > anchor.line);
      if (next) {
        const lines = md.split('\n');
        lines.splice(next.line, 0, `${'#'.repeat(level)} [New section]`, '');
        return { ...state, markdown: lines.join('\n') };
      }
    }
    return { ...state };
  }
  // Wall of text → insert a subheading at the paragraph boundary nearest
  // the middle of the first over-limit heading-less block.
  const outline = parseOutline(md);
  const lines = md.split('\n');
  const under = typeof payload.under === 'string' ? payload.under : null;
  const anchor = under ? outline.find((h) => h.text === under) : undefined;
  const start = anchor ? anchor.line + 1 : 0;
  const nextHeading = outline.find((h) => h.line >= start);
  const end = nextHeading && !anchor ? nextHeading.line : anchor
    ? (outline.find((h) => h.line > anchor.line)?.line ?? lines.length)
    : lines.length;
  const level = Math.min(6, (anchor?.level ?? 1) + 1);
  // Paragraph boundaries = blank lines inside the block.
  const blanks: number[] = [];
  for (let i = start; i < end; i++) {
    if (lines[i].trim().length === 0) blanks.push(i);
  }
  const usable = blanks.filter((i) => i > start && i < end - 1);
  if (usable.length > 0) {
    const mid = (start + end) / 2;
    const at = usable.reduce((a, b) => (Math.abs(b - mid) < Math.abs(a - mid) ? b : a));
    lines.splice(at + 1, 0, `${'#'.repeat(level)} [Subheading: name this section]`, '');
    return { ...state, markdown: lines.join('\n') };
  }
  // Single giant paragraph: break it at a sentence boundary, heading between.
  const blockText = lines.slice(start, end).join('\n');
  const re = /[.!?]\s+/g;
  const boundaries: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(blockText)) !== null) boundaries.push(m.index + 1);
  if (boundaries.length === 0) return { ...state };
  const mid = blockText.length / 2;
  const cut = boundaries.reduce((a, b) => (Math.abs(b - mid) < Math.abs(a - mid) ? b : a));
  const rebuilt = `${blockText.slice(0, cut).trim()}\n\n${'#'.repeat(level)} [Subheading: name this section]\n\n${blockText.slice(cut).trim()}`;
  const out = [...lines.slice(0, start), '', rebuilt, '', ...lines.slice(end)];
  return { ...state, markdown: out.join('\n') };
}

function fixAddFaqSchema(state: EditorState): EditorState {
  const pairs = extractQaPairs(state.markdown);
  const jsonLd = buildFaqJsonLd(pairs);
  // Fenced block: visible artifact for the writer, stripped from prose by
  // the scorer so attaching schema never distorts keyword/readability dims.
  const scaffold = `\n\n\`\`\`json\n${jsonLd}\n\`\`\``;
  return { ...state, markdown: `${state.markdown}${scaffold}` };
}

function fixAddInternalLink(state: EditorState, payload: Record<string, unknown>): EditorState {
  const slug = String(payload.slug ?? '').trim();
  const title = String(payload.title ?? slug).trim();
  if (slug.length === 0) return { ...state };
  return { ...state, markdown: `${state.markdown}\n\nRelated: [${title}](/${slug})` };
}

/**
 * Execute one prioritized fix as a deterministic in-editor transform.
 * Looks the fix up by id in the CURRENT score (so stale ids no-op safely),
 * and always returns a NEW EditorState — the input is never mutated.
 */
export function applyFix(state: EditorState, fixId: string): EditorState {
  const fix = scoreState(state).prioritizedFixes.find((f) => f.id === fixId);
  if (!fix) return { ...state };
  switch (fix.kind) {
    case 'add-term':
      return fixAddTerm(state, fix.payload);
    case 'shorten-title':
      return fixShorten(state, fix.payload);
    case 'add-answer':
      return fixAddAnswer(state, fix.payload);
    case 'split-paragraph':
      return fixSplitParagraph(state);
    case 'add-heading':
      return fixAddHeading(state, fix.payload);
    case 'add-faq-schema':
      return fixAddFaqSchema(state);
    case 'add-internal-link':
      return fixAddInternalLink(state, fix.payload);
    default:
      return { ...state };
  }
}
